import React, { useEffect, useRef, useState } from "react";
import Image from "next/image";
import icon from "./assets/icon.svg";
import { Figure } from "../shared/utils/Figure";
import useDebounce from "../hapAnalytics/utils/useDebounce";
import { config as axiosConfig } from "../shared/config";
import axios from "axios";
import extractAxiosMessage from "../shared/utils/extractAxiosError";
import createSearchSuggestion from "./createSearchSuggestion";
import { PiCaretRightBold } from "react-icons/pi";
import pages from "./pages";
import { useRouter } from "next/router";
import { VscSend } from "react-icons/vsc";
import { useSas } from "../../lib/store/useSasStore";

import {
  SearchDropdown,
  SearchDropdownInner,
  SectionContent,
  SectionContentInner,
} from "./styles";

import endpoints from "../../utility/endpoints";

const IndexSearchContainer = () => {
  const [searchResults, setSearchResults] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const inputRef = useRef(null);
  const [minimize, setMinimize] = useState([]);
  const {sasToken}=useSas()

  const router = useRouter();

  const debouncedSearch = useDebounce(searchTerm, 500);

  const fous = () => {
    inputRef.current.focus();
  };

  useEffect(() => {
    if (typeof window !== "undefined") {
      document.body.style.overflow = "hidden";
    }

    return () => {
      if (typeof window !== "undefined") {
        document.body.style.overflow = "unset";
      }
    };
  }, []);

  const handleMinimize = (key) => {
    if (minimize.includes(key)) {
      setMinimize(minimize.filter((item) => item !== key));
    } else {
      setMinimize([...minimize, key]);
    }
  };

  const goToLink = (link) => {
    if (!link) return;
    window.open(link, "_blank");
  };

  const handleSearch = async () => {
    let auth;

    if (typeof window !== "undefined") {
      auth = JSON.parse(localStorage.getItem("auth")).data;
    }

    try {
      const res = await axios.get(
        `${axiosConfig.baseUrl}/index-search?q=${debouncedSearch}`,
        {
          headers: {
            Authorization: auth?.accessToken,
          },
        },
      );

      if (res.data?.status === "success") {
        setSearchResults(createSearchSuggestion(res.data?.data));
      }

      if (res.data?.message === "Error while authentication") {
        router.push("/login");
      }
    } catch (error) {
      const message = extractAxiosMessage(error);
      console.error("message", message);
    }
  };

  const loadSearchPage = () => {
    if (!searchTerm) return;
    router.push({
      pathname: "/search",
      query: { q: searchTerm },
    });
  };

  useEffect(() => {
    if (debouncedSearch) {
      handleSearch();
    } else {
      setSearchResults({});
    }
  }, [debouncedSearch]);

  return (
    <div
      style={{
        background:
          "linear-gradient(356deg, rgba(192, 207, 252, 1) 0%, rgba(214, 227, 252, 1) 30%, rgba(227, 235, 252, 1) 70%, rgba(229, 240, 252, 1) 100%)",
      }}
      className="max-w-screen max-h-screen w-screen h-screen flex justify-center items-center overflow-hidden fixed top-0 left-0"
    >
      <div className="min-w-[26rem] w-[60%]">
        <div className="flex justify-center items-center gap-4">
          <video
            style={{ mixBlendMode: "multiply" }}
            autoPlay
            loop
            muted
            src={`https://bnlwestgunileveraf01092.blob.core.windows.net/botstorevideo/hap_home/HAP.mp4?${sasToken}`}
            className="w-[350px] h-[30px] scale-[1.1]"
          ></video>
        </div>

        <div
          onClick={(e) => {
            e.stopPropagation();
            focus();
          }}
          className="w-full !border !border-[#C8D7FF] flex items-center gap-3 p-1 bg-white rounded-full mt-3 relative shadow-sm"
        >
          <Figure className="ml-2 w-7 h-7 animate-spin">
            <Image src={icon} />
          </Figure>
          <input
            onChange={(e) => setSearchTerm(e.target.value)}
            value={searchTerm}
            ref={inputRef}
            type="text"
            className="w-full outline-none h-full text-[17px] font-unilever-shilling-normal"
          />
          <button
            onClick={(e) => {
              e.stopPropagation();
              loadSearchPage();
            }}
            className="aspect-square w-12 h-12 bg-[rgba(200,215,255,0.5)] rounded-full flex justify-center items-center"
          >
            <VscSend className="text-primary w-6 h-6" />
          </button>

          <SearchDropdown
            $open={Object.keys(searchResults).length > 0}
            className="absolute left-0 top-16 w-full rounded-xl bg-white shadow-lg z-50"
          >
            <SearchDropdownInner>
              {Object.keys(searchResults).map((key) => {
                if (!searchResults[key].length) return null;

                const isOpen = !minimize.includes(key);

                return (
                  <div key={key} className="p-3 border-b last:border-b-0">
                    <div className="flex items-center gap-2">
                      <span className="text-primary">{key}</span>

                      <PiCaretRightBold
                        onClick={() => handleMinimize(key)}
                        className={`cursor-pointer transition-transform duration-300 text-primary ${
                          isOpen ? "rotate-90" : ""
                        }`}
                      />
                    </div>

                    <SectionContent $open={isOpen}>
                      <SectionContentInner>
                        <div className="p-2 flex flex-col gap-2">
                          {searchResults[key].map((item, index) => (
                            <div
                              key={index}
                              onClick={() => goToLink(item?.link)}
                              className="text-[12px] p-2 rounded-md cursor-pointer hover:bg-[rgba(200,215,255,0.2)] transition-colors"
                            >
                              {key === "Bots"
                                ? `Bot ID: ${item?.botExternalId} Process Name: ${item?.name}`
                                : item?.name}
                            </div>
                          ))}
                        </div>
                      </SectionContentInner>
                    </SectionContent>
                  </div>
                );
              })}
            </SearchDropdownInner>
          </SearchDropdown>
        </div>
        <div className="flex flex-wrap items-center justify-center gap-3 mt-4">
          {pages.map((item, idx) => (
            <div
              onClick={() => router.push(`/${item.href}`)}
              key={idx}
              className="text-[12px] px-4 py-2 rounded-full !border !border-[#C8D7FF] bg-white cursor-pointer text-primary hover:scale-[1.15] transition-all duration-300 shadow-sm"
            >
              {item?.label}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default IndexSearchContainer;

